import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Edit, Trash2, Play, Pause, CheckCircle, Clock, User, Settings, Calendar, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import ManufacturingService from '@/api/manufacturing.service';
import { canView, canEdit, canDelete } from '@/lib/permissions';
import { format } from 'date-fns';

interface WorkOrder {
  id: number;
  publicId: string;
  businessId: number;
  workOrderNumber: string;
  productionJobId: number;
  productionJobNumber: string;
  operation: string;
  description?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  assignedTo?: string;
  estimatedHours: number;
  actualHours?: number;
  scheduledStartDate: string;
  scheduledEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  notes?: string;
  dateCreated: string;
  dateUpdated: string;
}

interface TimeEntry {
  id: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  notes?: string;
  employee: string;
}

const WorkOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [timeEntry, setTimeEntry] = useState({ hours: '', notes: '', employee: '' });
  const [completionNotes, setCompletionNotes] = useState('');

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    if (id) {
      fetchWorkOrder();
      fetchTimeEntries();
    }
  }, [id]);

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      const response = await ManufacturingService.getWorkOrder(id!);
      
      if (response.data.result === 'success') {
        setWorkOrder(response.data.workOrder);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch work order",
          variant: "destructive",
        });
        navigate('/manufacturing/work-orders');
      }
    } catch (error) {
      console.error('Error fetching work order:', error);
      toast({
        title: "Error",
        description: "Failed to fetch work order",
        variant: "destructive",
      });
      navigate('/manufacturing/work-orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeEntries = async () => {
    // Mock time entries - in real app, this would come from an API
    const mockTimeEntries: TimeEntry[] = [
      {
        id: '1',
        startTime: '2024-01-15T08:00:00Z',
        endTime: '2024-01-15T12:00:00Z',
        duration: 4,
        notes: 'Initial setup and preparation',
        employee: 'John Smith'
      },
      {
        id: '2',
        startTime: '2024-01-15T13:00:00Z',
        endTime: '2024-01-15T17:00:00Z',
        duration: 4,
        notes: 'Main operation work',
        employee: 'John Smith'
      }
    ];
    setTimeEntries(mockTimeEntries);
  };

  const handleStatusUpdate = async (status: string) => {
    if (!workOrder) return;

    try {
      setIsUpdating(true);
      const response = await ManufacturingService.updateWorkOrderStatus(workOrder.publicId, { status });
      
      if (response.data.result === 'success') {
        setWorkOrder({ ...workOrder, status: status as any });
        toast({
          title: "Success",
          description: `Work order status updated to ${status}`,
        });
      } else {
        throw new Error(response.data.message || 'Failed to update status');
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update status",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStartWork = () => {
    handleStatusUpdate('in_progress');
  };

  const handleCompleteWork = async () => {
    if (!workOrder) return;

    try {
      setIsUpdating(true);
      const response = await ManufacturingService.updateWorkOrderStatus(workOrder.publicId, { 
        status: 'completed',
        actualEndDate: new Date().toISOString(),
        notes: completionNotes
      });
      
      if (response.data.result === 'success') {
        setWorkOrder({ 
          ...workOrder, 
          status: 'completed',
          actualEndDate: new Date().toISOString(),
          notes: completionNotes
        });
        toast({
          title: "Success",
          description: "Work order completed successfully",
        });
        setShowCompletionDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to complete work order');
      }
    } catch (error: any) {
      console.error('Error completing work order:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to complete work order",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddTimeEntry = () => {
    const newEntry: TimeEntry = {
      id: Date.now().toString(),
      startTime: new Date().toISOString(),
      duration: parseFloat(timeEntry.hours),
      notes: timeEntry.notes,
      employee: timeEntry.employee || 'Current User'
    };
    
    setTimeEntries([...timeEntries, newEntry]);
    setTimeEntry({ hours: '', notes: '', employee: '' });
    setShowTimeDialog(false);
    
    toast({
      title: "Success",
      description: "Time entry added successfully",
    });
  };

  const handleDelete = async () => {
    if (!workOrder) return;

    try {
      setIsUpdating(true);
      const response = await ManufacturingService.deleteWorkOrder(workOrder.publicId);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Work order deleted successfully",
        });
        navigate('/manufacturing/work-orders');
      } else {
        throw new Error(response.data.message || 'Failed to delete work order');
      }
    } catch (error: any) {
      console.error('Error deleting work order:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete work order",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
      setShowDeleteDialog(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'outline' as const, label: 'Pending', color: 'text-yellow-600' },
      in_progress: { variant: 'default' as const, label: 'In Progress', color: 'text-blue-600' },
      completed: { variant: 'default' as const, label: 'Completed', color: 'text-green-600' },
      cancelled: { variant: 'destructive' as const, label: 'Cancelled', color: 'text-red-600' },
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    return <Badge variant={config.variant} className={config.color}>{config.label}</Badge>;
  };

  const getProgressPercentage = () => {
    if (!workOrder) return 0;
    
    switch (workOrder.status) {
      case 'pending': return 0;
      case 'in_progress': return 50;
      case 'completed': return 100;
      case 'cancelled': return 0;
      default: return 0;
    }
  };

  const getTotalActualHours = () => {
    return timeEntries.reduce((total, entry) => total + (entry.duration || 0), 0);
  };

  // Check permissions
  if (!canView('Manufacturing')) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/work-orders')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Work Orders
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Permission Denied</h3>
              <p className="text-muted-foreground">
                You don't have permission to view work orders.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/work-orders')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Work Orders
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Loading work order...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!workOrder) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/work-orders')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Work Orders
          </Button>
        </div>
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="text-center">
              <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Work Order Not Found</h3>
              <p className="text-muted-foreground">
                The work order you're looking for doesn't exist.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/manufacturing/work-orders')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Work Orders
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{workOrder.workOrderNumber}</h1>
            <p className="text-muted-foreground">
              {workOrder.operation} - {workOrder.productionJobNumber}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {workOrder.status === 'pending' && (
            <Button
              onClick={handleStartWork}
              disabled={isUpdating}
              className="flex items-center gap-2"
            >
              <Play className="h-4 w-4" />
              Start Work
            </Button>
          )}
          {workOrder.status === 'in_progress' && (
            <Button
              onClick={() => setShowCompletionDialog(true)}
              disabled={isUpdating}
              className="flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Complete
            </Button>
          )}
          {canEdit('Manufacturing') && workOrder.status === 'pending' && (
            <Button
              variant="outline"
              onClick={() => navigate(`/manufacturing/work-orders/${workOrder.publicId}/edit`)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          )}
          {canDelete('Manufacturing') && workOrder.status === 'pending' && (
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(true)}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Status and Progress */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Work Order Status
            </CardTitle>
            {getStatusBadge(workOrder.status)}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span>Progress</span>
              <span>{getProgressPercentage()}%</span>
            </div>
            <Progress value={getProgressPercentage()} className="w-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Estimated Hours</p>
              <p className="font-medium">{workOrder.estimatedHours}h</p>
            </div>
            <div>
              <p className="text-muted-foreground">Actual Hours</p>
              <p className="font-medium">{getTotalActualHours()}h</p>
            </div>
            <div>
              <p className="text-muted-foreground">Assigned To</p>
              <p className="font-medium">{workOrder.assignedTo || 'Unassigned'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Efficiency</p>
              <p className="font-medium">
                {getTotalActualHours() > 0 
                  ? `${Math.round((workOrder.estimatedHours / getTotalActualHours()) * 100)}%`
                  : 'N/A'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="time-tracking">Time Tracking</TabsTrigger>
          <TabsTrigger value="schedule">Schedule</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Work Order Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Work Order Number</Label>
                    <p className="text-sm">{workOrder.workOrderNumber}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Production Job</Label>
                    <p className="text-sm">{workOrder.productionJobNumber}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Operation</Label>
                    <p className="text-sm">{workOrder.operation}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Assigned To</Label>
                    <p className="text-sm">{workOrder.assignedTo || 'Unassigned'}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Status</Label>
                    <div className="mt-1">{getStatusBadge(workOrder.status)}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Estimated Hours</Label>
                    <p className="text-sm">{workOrder.estimatedHours} hours</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Created</Label>
                    <p className="text-sm">{format(new Date(workOrder.dateCreated), 'PPP')}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Last Updated</Label>
                    <p className="text-sm">{format(new Date(workOrder.dateUpdated), 'PPP')}</p>
                  </div>
                </div>
              </div>
              
              {workOrder.description && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Description</Label>
                    <p className="text-sm mt-1">{workOrder.description}</p>
                  </div>
                </>
              )}
              
              {workOrder.notes && (
                <>
                  <Separator />
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Notes</Label>
                    <p className="text-sm mt-1">{workOrder.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="time-tracking" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Time Entries
                </CardTitle>
                <Button
                  onClick={() => setShowTimeDialog(true)}
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Clock className="h-4 w-4" />
                  Add Time
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {timeEntries.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Time Entries</h3>
                  <p className="text-muted-foreground">
                    Start tracking time for this work order.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {timeEntries.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <User className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{entry.employee}</p>
                          <p className="text-sm text-muted-foreground">
                            {entry.duration}h - {entry.notes || 'No notes'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(entry.startTime), 'PPP')}
                        </p>
                        {entry.endTime && (
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(entry.startTime), 'p')} - {format(new Date(entry.endTime), 'p')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <Separator />
                  
                  <div className="flex justify-between items-center font-medium">
                    <span>Total Hours:</span>
                    <span>{getTotalActualHours()}h</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedule" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Schedule Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Scheduled Start</Label>
                    <p className="text-sm">{format(new Date(workOrder.scheduledStartDate), 'PPP')}</p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-muted-foreground">Scheduled End</Label>
                    <p className="text-sm">{format(new Date(workOrder.scheduledEndDate), 'PPP')}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {workOrder.actualStartDate && (
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Actual Start</Label>
                      <p className="text-sm">{format(new Date(workOrder.actualStartDate), 'PPP')}</p>
                    </div>
                  )}
                  {workOrder.actualEndDate && (
                    <div>
                      <Label className="text-sm font-medium text-muted-foreground">Actual End</Label>
                      <p className="text-sm">{format(new Date(workOrder.actualEndDate), 'PPP')}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Schedule Status */}
              <Separator />
              <div className="flex items-center gap-2">
                {new Date(workOrder.scheduledEndDate) < new Date() && workOrder.status !== 'completed' ? (
                  <>
                    <AlertCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">Behind Schedule</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600">On Schedule</span>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Work Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this work order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isUpdating}>
              {isUpdating ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Entry Dialog */}
      <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Time Entry</DialogTitle>
            <DialogDescription>
              Record time spent on this work order.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="hours">Hours</Label>
              <Input
                id="hours"
                type="number"
                step="0.5"
                min="0.1"
                value={timeEntry.hours}
                onChange={(e) => setTimeEntry({ ...timeEntry, hours: e.target.value })}
                placeholder="Enter hours worked"
              />
            </div>
            <div>
              <Label htmlFor="employee">Employee</Label>
              <Input
                id="employee"
                value={timeEntry.employee}
                onChange={(e) => setTimeEntry({ ...timeEntry, employee: e.target.value })}
                placeholder="Employee name"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={timeEntry.notes}
                onChange={(e) => setTimeEntry({ ...timeEntry, notes: e.target.value })}
                placeholder="What was accomplished?"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddTimeEntry}
              disabled={!timeEntry.hours || parseFloat(timeEntry.hours) <= 0}
            >
              Add Time Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion Dialog */}
      <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete Work Order</DialogTitle>
            <DialogDescription>
              Mark this work order as completed. Add any final notes below.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="completionNotes">Completion Notes</Label>
              <Textarea
                id="completionNotes"
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="Any final notes or observations..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompletionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCompleteWork} disabled={isUpdating}>
              {isUpdating ? 'Completing...' : 'Complete Work Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrderDetail; 